import { ObjectType } from '@policyfly/protobuf/pfcapability'
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { RpcError } from '@protobuf-ts/runtime-rpc'

import { useApiStore } from '@/stores/api'
import { useAuthenticationStore } from '@/stores/authentication'
import { useCapabilityStore } from '@/stores/capability'
import { useFormApplicationStore } from '@/stores/form/application'
import { useLoginStore } from '@/stores/login'
import { useNotificationStore } from '@/stores/notification'
import { useOnboardingStore } from '@/stores/onboarding'
import { useSettingsStore } from '@/stores/settings'
import { useSignatureStore } from '@/stores/signature'

import routeNames from './routeNames'
import { extractApiError } from '@/utils/api'

import type { NavigationGuard, RouteLocationNormalizedGeneric } from 'vue-router'

function routeRequiresAuth (to: RouteLocationNormalizedGeneric): boolean {
  return to.matched.some((route) => route.meta.requiresAuth)
}

/* Global Guards (in order of priority) */

/**
 * Ensures the user is authenticated and has a valid token if the route requires it.
 * Will log out the user and redirect to login if the token is invalid or expired.
 */
export const authenticationGuard: NavigationGuard = async (to) => {
  if (!routeRequiresAuth(to)) return

  const apiStore = useApiStore()
  const loginStore = useLoginStore()
  if (apiStore.isLoggedIn) {
    return
  }
  // eslint-disable-next-line no-console
  console.log('Not logged in, redirecting to Login')
  const authenticationStore = useAuthenticationStore()
  authenticationStore.$reset()
  apiStore.logout()
  loginStore.targetPath = to.fullPath
  return { name: routeNames.LOGIN }
}

/**
 * Ensures the "program" query param is always set on authenticated routes.
 * Will also switch to the program in the query if different.
 */
export const programQueryGuard: NavigationGuard = async (to) => {
  const { query } = to
  const programSlug = query.program
  const authenticationStore = useAuthenticationStore()
  const requiresAuth = routeRequiresAuth(to)

  // Query param exists, ensure correct program has been selected
  if (programSlug) {
    const { programs, legacyId } = authenticationStore
    const foundProgram = programs.find((p) => p.slug === programSlug)

    if (foundProgram) {
      authenticationStore.programIds = {
        id: foundProgram.id,
        bingoId: foundProgram.bingoId,
      }
    } else {
      const notificationStore = useNotificationStore()
      console.error(`User ${legacyId} attempted to access program ${programSlug} but no subscription was found.`)
      notificationStore.snackbar.error('You are not a member of the requested program.')
      return { name: routeNames.HOME }
    }
  } else if (requiresAuth) {
    if (authenticationStore.currentProgram) {
      return { ...to, query: { ...query, program: String(authenticationStore.currentProgram!.slug) } }
    } else {
      const defaultProgram = authenticationStore.programs.find((p) => p.defaultProgram) ?? authenticationStore.programs[0]
      if (defaultProgram) {
        return { ...to, query: { ...query, program: String(defaultProgram.slug) } }
      } else {
        console.error('No currently selected program to include in querystring.')
      }
    }
  }
}

/**
 * Will load the program settings on authenticated routes before continuing.
 * Redirects to login if token renewal fails.
 */
export const programSettingsGuard: NavigationGuard = async (to) => {
  if (!routeRequiresAuth(to)) return

  try {
    const settingsStore = useSettingsStore()
    await settingsStore.load()
  } catch (err) {
    if (err instanceof RpcError && err.code === GrpcStatusCode[GrpcStatusCode.UNAUTHENTICATED]) {
      // eslint-disable-next-line no-console
      console.log('Token Renewal Failed, redirecting to Login')
      const authenticationStore = useAuthenticationStore()
      const apiStore = useApiStore()
      authenticationStore.$reset()
      apiStore.logout()
      return { name: routeNames.LOGIN }
    }
    const notificationStore = useNotificationStore()
    console.error(err)
    void notificationStore.confirm({
      title: 'Program Load Error',
      body: 'This program could not be loaded. Please try reloading the page. If this error persists please contact support.',
      persistent: true,
      onlyConfirm: true,
    })
  }
}

/**
 * Loads the capabilities for authenticated routes before continuing.
 * Scoped to the application or policy in the route params, or only global capabilities otherwise.
 */
export const capabilityGuard: NavigationGuard = async (to) => {
  if (!routeRequiresAuth(to)) return

  const capabilityStore = useCapabilityStore()
  const { applicationId, policyId } = to.params
  if (typeof applicationId === 'string' && applicationId) {
    await capabilityStore.load({ type: ObjectType.OBJECT_TYPE_APPLICATION, id: applicationId })
  } else if (typeof policyId === 'string' && policyId) {
    await capabilityStore.load({ type: ObjectType.OBJECT_TYPE_POLICY, id: policyId })
  } else {
    await capabilityStore.load()
  }
}

/* Per-Route Guards */
export const registrationGuard: NavigationGuard = async (to) => {
  const { name, query } = to
  const onboardingStore = useOnboardingStore()

  if (name === routeNames.REGISTER) {
    const { token, email } = query
    if (!token || !email) return { name: routeNames.LOGIN }
    try {
      await onboardingStore.loadUser({
        token: token as string,
        email: email as string,
      })
    } catch (err) {
      console.error('User unable to register', email, token, err)
      const notificationStore = useNotificationStore()
      notificationStore.snackbar.error(extractApiError(err, 'An unknown error occurred.'))
      return { name: routeNames.LOGIN }
    }
  } else {
    if (!onboardingStore.loaded) return { name: routeNames.LOGIN }
  }
}

export const accountGuard: NavigationGuard = async () => {
  const authenticationStore = useAuthenticationStore()
  if (!authenticationStore.loaded) {
    try {
      await authenticationStore.load()
    } catch {
      return { name: routeNames.HOME }
    }
  }
}

export const superuserGuard: NavigationGuard = () => {
  const authenticationStore = useAuthenticationStore()
  const notificationStore = useNotificationStore()
  if (!authenticationStore.isSuperuser) {
    notificationStore.snackbar.error('You do not have permission to access this page.')
    return { name: routeNames.HOME }
  }
}

export const signatureGuard: NavigationGuard = async (to) => {
  const signatureStore = useSignatureStore()
  const { name, query } = to
  if (name === routeNames.SIGNATURE_DETAILS) {
    const { luid, program } = query
    if (!luid || !program) return { name: routeNames.LOGIN }
    signatureStore.program = program as string
    const res = await signatureStore.fetchAttachment(luid as string)
    // @ts-expect-error(external): This is probably a typo, should be looked at
    if (!res || res.signature_status === 'SIGNED') {
      return { name: routeNames.SIGNATURE_EXPIRED }
    }
  } else {
    if (!signatureStore.hasParams) return { name: routeNames.LOGIN }
  }
}

export const applicationStartGuard: NavigationGuard = async () => {
  const formApplicationStore = useFormApplicationStore()
  await formApplicationStore.loadData()
}

export const loggedInGuard: NavigationGuard = () => {
  const apiStore = useApiStore()
  if (apiStore.isLoggedIn) {
    return { name: routeNames.HOME }
  }
}
